import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, LogOut, Mail, User, Calendar, MessageSquare } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const navigate = useNavigate();

  // Check if already authenticated (session storage)
  useEffect(() => {
    const auth = sessionStorage.getItem('metalove_admin_auth');
    if (auth === 'true') {
      setIsAuthenticated(true);
      fetchApplications();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login`, { password });
      setIsAuthenticated(true);
      sessionStorage.setItem('metalove_admin_auth', 'true');
      setError('');
      fetchApplications();
    } catch (err) {
      setError('Mot de passe incorrect');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('metalove_admin_auth');
    navigate('/');
  };

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/applications`);
      setApplications(response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      ));
    } catch (err) {
      console.error('Error fetching applications:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: '#050A14' }}
      >
        <div className="w-full max-w-sm mx-6">
          <div className="text-center mb-10">
            <div 
              className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center"
              style={{ border: '1px solid rgba(156, 163, 175, 0.2)' }}
            >
              <Lock className="w-7 h-7" style={{ color: '#9CA3AF' }} strokeWidth={1.5} />
            </div>
            <h1 
              className="font-heading text-3xl mb-2"
              style={{ color: '#D1D5DB' }}
            >
              Administration
            </h1>
            <p style={{ color: '#6B7280' }} className="text-sm">
              Accès réservé à l'équipe MetaLove
            </p>
          </div>

          <form onSubmit={handleLogin}>
            <div className="relative mb-6">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mot de passe"
                className="w-full bg-transparent text-base py-4 px-0 font-body focus:outline-none text-center"
                style={{ 
                  color: '#D1D5DB',
                  borderBottom: '1px solid rgba(156, 163, 175, 0.2)'
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-0 top-1/2 -translate-y-1/2 p-2"
                style={{ color: '#6B7280' }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {error && (
              <p className="text-center text-sm mb-4" style={{ color: '#EF4444' }}>
                {error}
              </p>
            )}

            <button
              type="submit"
              className="btn-silver w-full"
            >
              Accéder
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div 
      className="min-h-screen pt-8 pb-16"
      style={{ backgroundColor: '#050A14' }}
    >
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-12">
          <div>
            <h1 
              className="font-heading text-3xl mb-1"
              style={{ color: '#D1D5DB' }}
            >
              Candidatures
            </h1>
            <p style={{ color: '#6B7280' }} className="text-sm">
              {applications.length} candidature{applications.length > 1 ? 's' : ''} reçue{applications.length > 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm transition-colors"
            style={{ color: '#6B7280' }}
          >
            <LogOut size={18} />
            <span>Déconnexion</span>
          </button>
        </div>

        {loading ? (
          <p style={{ color: '#6B7280' }} className="text-center py-20">
            Chargement...
          </p>
        ) : applications.length === 0 ? (
          <div className="text-center py-20">
            <p style={{ color: '#6B7280' }}>
              Aucune candidature pour le moment.
            </p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Applications List */}
            <div className="lg:col-span-1 space-y-3">
              {applications.map((app) => (
                <button
                  key={app.id}
                  onClick={() => setSelectedApp(app)}
                  className={`w-full text-left p-4 rounded-sm transition-all ${
                    selectedApp?.id === app.id ? 'ring-1' : ''
                  }`}
                  style={{ 
                    backgroundColor: selectedApp?.id === app.id ? '#101828' : '#0A0F18',
                    border: '1px solid rgba(156, 163, 175, 0.08)',
                    ringColor: 'rgba(156, 163, 175, 0.3)'
                  }}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <User size={16} style={{ color: '#6B7280' }} />
                    <span style={{ color: '#D1D5DB' }} className="font-medium">
                      {app.first_name}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mb-2">
                    <Mail size={14} style={{ color: '#4B5563' }} />
                    <span style={{ color: '#6B7280' }} className="text-sm truncate">
                      {app.email}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar size={14} style={{ color: '#4B5563' }} />
                    <span style={{ color: '#4B5563' }} className="text-xs">
                      {formatDate(app.created_at)}
                    </span>
                  </div>
                </button>
              ))}
            </div>

            {/* Application Detail */}
            <div 
              className="lg:col-span-2 p-6 rounded-sm"
              style={{ 
                backgroundColor: '#0A0F18',
                border: '1px solid rgba(156, 163, 175, 0.08)'
              }}
            >
              {selectedApp ? (
                <div>
                  {/* Header */}
                  <div className="mb-8 pb-6" style={{ borderBottom: '1px solid rgba(156, 163, 175, 0.1)' }}>
                    <h2 
                      className="font-heading text-2xl mb-2"
                      style={{ color: '#D1D5DB' }}
                    >
                      {selectedApp.first_name}
                    </h2>
                    <p style={{ color: '#9CA3AF' }} className="mb-1">{selectedApp.email}</p>
                    <p style={{ color: '#6B7280' }} className="text-sm">
                      {formatDate(selectedApp.created_at)} • {selectedApp.language.toUpperCase()}
                    </p>
                  </div>

                  {/* Answers */}
                  <div className="space-y-6">
                    <div>
                      <p style={{ color: '#6B7280' }} className="text-xs uppercase tracking-wider mb-2">
                        Qu'est-ce qui vous amène ici ?
                      </p>
                      <p style={{ color: '#D1D5DB' }} className="leading-relaxed">
                        {selectedApp.answers.question1}
                      </p>
                    </div>
                    <div>
                      <p style={{ color: '#6B7280' }} className="text-xs uppercase tracking-wider mb-2">
                        Comment décririez-vous votre quête ?
                      </p>
                      <p style={{ color: '#D1D5DB' }} className="leading-relaxed">
                        {selectedApp.answers.question2}
                      </p>
                    </div>
                    <div>
                      <p style={{ color: '#6B7280' }} className="text-xs uppercase tracking-wider mb-2">
                        Qu'attendez-vous d'une rencontre ?
                      </p>
                      <p style={{ color: '#D1D5DB' }} className="leading-relaxed">
                        {selectedApp.answers.question3}
                      </p>
                    </div>
                    <div>
                      <p style={{ color: '#6B7280' }} className="text-xs uppercase tracking-wider mb-2">
                        Une phrase qui vous définit
                      </p>
                      <p style={{ color: '#D1D5DB' }} className="leading-relaxed italic">
                        "{selectedApp.answers.question4}"
                      </p>
                    </div>
                    <div>
                      <p style={{ color: '#6B7280' }} className="text-xs uppercase tracking-wider mb-2">
                        Prêt(e) à faire confiance au destin ?
                      </p>
                      <p style={{ color: '#D1D5DB' }} className="leading-relaxed">
                        {selectedApp.answers.question5}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-8 pt-6 flex gap-4" style={{ borderTop: '1px solid rgba(156, 163, 175, 0.1)' }}>
                    <a
                      href={`mailto:${selectedApp.email}?subject=MetaLove - Votre candidature`}
                      className="btn-outline text-sm"
                    >
                      <Mail size={16} className="mr-2 inline" />
                      Répondre par email
                    </a>
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center py-20">
                  <div className="text-center">
                    <MessageSquare size={40} style={{ color: '#4B5563' }} className="mx-auto mb-4" />
                    <p style={{ color: '#6B7280' }}>
                      Sélectionnez une candidature pour voir les détails
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
